import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import DashboardShell from "../components/DashboardShell";
import { toast } from "sonner";
import { Plus, X, Power, KeyRound, UserCog } from "lucide-react";
import { useBranch } from "../context/BranchContext";
import EmployeePasswordDialog from "../components/EmployeePasswordDialog";

const PERM_GROUPS = [
  { title: "الطلبات", perms: [
    ["orders_view", "مشاهدة الطلبات"],
    ["orders_create", "إنشاء طلب هاتفي"],
    ["orders_update_status", "تعديل حالة الطلب"],
    ["orders_cancel", "إلغاء الطلب"],
  ]},
  { title: "المنيو", perms: [
    ["products_add", "إضافة أصناف"],
    ["products_edit", "تعديل الأصناف"],
    ["prices_edit", "تعديل الأسعار", "products_edit"],
    ["addons_edit", "تعديل الإضافات", "products_edit"],
    ["products_delete", "حذف الأصناف"],
  ]},
  { title: "العملاء والتقارير", perms: [
    ["customers_view", "مشاهدة بيانات العملاء"],
    ["reports_view", "مشاهدة التقارير"],
  ]},
  { title: "إعدادات المحل", perms: [
    ["hours_edit", "تعديل ساعات العمل"],
    ["availability_toggle", "إيقاف/تشغيل استقبال الطلبات"],
    ["qr_manage", "إدارة QR والرابط"],
  ]},
];

const DEFAULT_PERMS = {
  orders_view: true, orders_update_status: true,
  orders_create: false, orders_cancel: false,
  customers_view: false, reports_view: false,
  products_add: false, products_edit: false, products_delete: false,
  prices_edit: false, addons_edit: false,
  hours_edit: false, availability_toggle: false, qr_manage: false, employees_manage: false,
};

export default function Employees() {
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [passwordFor, setPasswordFor] = useState(null);
  const load = () => api.get("/employees").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const toggleActive = async (e) => {
    try {
      if (e.active === false) await api.patch(`/employees/${e.id}`, { active: true });
      else await api.delete(`/employees/${e.id}`);
      load();
    } catch (er) { toast.error(formatErr(er)); }
  };

  return (
    <DashboardShell title="الموظفون">
      <button onClick={() => setAdding(true)} className="w-full pill brand-grad text-white font-bold py-3 mb-4 tap-target inline-flex items-center justify-center gap-2" data-testid="add-employee-btn">
        <Plus size={18}/> إضافة موظف
      </button>

      {list.length === 0 && (
        <div className="text-center bg-white rounded-3xl border border-orange-100 p-8" data-testid="employees-empty">
          <div className="text-5xl mb-2">👥</div>
          <div className="font-extrabold text-lg">لا يوجد موظفون بعد</div>
          <div className="text-sm text-gray-600 mt-1">أضف موظفاً وحدد صلاحياته بدقة</div>
        </div>
      )}

      <div className="space-y-3">
        {list.map(e => (
          <div key={e.id} className="bg-white rounded-2xl border border-orange-100 p-4" data-testid={`emp-${e.email}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-extrabold">{e.name}</div>
                <div className="text-xs text-gray-500 num-badge">{e.email}</div>
                {e.phone && <div className="text-xs text-gray-500 num-badge">{e.phone}</div>}
              </div>
              <span className={`pill text-xs font-bold px-3 py-1 ${e.active !== false ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                {e.active !== false ? "نشط" : "موقوف"}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => setEditing(e)} className="pill bg-orange-50 border border-orange-100 text-orange-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`edit-${e.email}`}><UserCog size={12}/> الصلاحيات</button>
              <button
                onClick={() => setPasswordFor(e)}
                className={
                  "pill inline-flex items-center gap-1 border border-orange-100 bg-white px-3 "
                  + "py-1.5 text-xs font-bold text-orange-700"
                }
                data-testid={`change-password-${e.email}`}
              >
                <KeyRound size={12}/> تغيير كلمة المرور
              </button>
              <button onClick={() => toggleActive(e)} className="pill bg-white border border-gray-200 text-gray-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid={`toggle-${e.email}`}><Power size={12}/> {e.active !== false ? "إيقاف" : "تفعيل"}</button>
            </div>
          </div>
        ))}
      </div>

      {(adding || editing) && (
        <EmployeeDialog initial={editing} onClose={() => { setAdding(false); setEditing(null); }} onSaved={() => { setAdding(false); setEditing(null); load(); }} />
      )}
      {passwordFor && (
        <EmployeePasswordDialog
          employee={passwordFor}
          onClose={() => setPasswordFor(null)}
          onSaved={() => {
            setPasswordFor(null);
            load();
          }}
        />
      )}
    </DashboardShell>
  );
}

function EmployeeDialog({ initial, onClose, onSaved }) {
  const branchCtx = useBranch();
  const branches = branchCtx?.branches || [];
  const multiBranch = branches.length > 1;
  const [f, setF] = useState({
    name: initial?.name || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    password: "",
    permissions: { ...DEFAULT_PERMS, ...(initial?.permissions || {}) },
    branch_ids: initial?.branch_ids || (branches.find(b => b.is_main) ? [branches.find(b => b.is_main).id] : []),
  });
  const [busy, setBusy] = useState(false);
  const setPerm = (k, v) => setF(s => ({ ...s, permissions: { ...s.permissions, [k]: v } }));
  const upd = (k, v) => setF(s => ({ ...s, [k]: v }));
  const toggleBranch = (id, on) => setF(s => ({ ...s, branch_ids: on ? [...new Set([...s.branch_ids, id])] : s.branch_ids.filter(x => x !== id) }));

  const save = async () => {
    if (!initial) {
      if (!f.name || !f.email || !f.password) return toast.error("أكمل الحقول");
      if (f.password.length < 6) return toast.error("كلمة المرور 6 أحرف على الأقل");
    }
    if (multiBranch && f.branch_ids.length === 0) return toast.error("حدد فرعًا واحدًا على الأقل للموظف");
    setBusy(true);
    try {
      if (initial) {
        const patch = { name: f.name, phone: f.phone, permissions: f.permissions };
        if (multiBranch) patch.branch_ids = f.branch_ids;
        await api.patch(`/employees/${initial.id}`, patch);
      } else {
        const body = { ...f };
        if (!multiBranch) delete body.branch_ids;
        await api.post("/employees", body);
      }
      toast.success("تم الحفظ"); onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-end sm:place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-5 rise-in max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg">{initial ? "تعديل الموظف" : "إضافة موظف"}</div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center"><X size={18}/></button>
        </div>
        <div className="space-y-2">
          <F label="اسم الموظف" v={f.name} on={v => upd("name", v)} tid="emp-name" />
          <F label="البريد الإلكتروني" type="email" v={f.email} on={v => upd("email", v)} tid="emp-email" disabled={!!initial} />
          <F label="رقم الجوال (اختياري)" v={f.phone} on={v => upd("phone", v)} tid="emp-phone" />
          {!initial && (
            <F
              label="كلمة المرور المؤقتة"
              type="password"
              v={f.password}
              on={(value) => upd("password", value)}
              tid="emp-password"
            />
          )}
        </div>

        {multiBranch && (
          <div className="mt-4" data-testid="emp-branches">
            <div className="font-extrabold mb-2">الفروع المصرّح بها</div>
            <div className="space-y-1">
              {branches.map(b => (
                <label key={b.id} className="flex items-center justify-between py-2 px-3 rounded-xl bg-orange-50/50 border border-orange-100">
                  <span className="text-sm">{b.name}{b.is_main && <span className="text-[10px] text-gray-500"> (رئيسي)</span>}{b.active === false && <span className="text-[10px] text-gray-500"> (متوقف)</span>}</span>
                  <input type="checkbox" checked={f.branch_ids.includes(b.id)} onChange={e => toggleBranch(b.id, e.target.checked)} className="w-5 h-5 accent-orange-600" data-testid={`emp-branch-${b.id}`} />
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <div className="font-extrabold mb-2">الصلاحيات</div>
          {PERM_GROUPS.map(g => (
            <div key={g.title} className="mb-3">
              <div className="text-xs font-bold text-gray-500 mb-1">{g.title}</div>
              <div className="space-y-1">
                {g.perms.map(([k, label, parent]) => {
                  const disabled = !!parent && !f.permissions[parent];
                  return (
                    <label
                      key={k}
                      className={
                        "flex items-center justify-between rounded-xl border border-orange-100 bg-orange-50/50 "
                        + "px-3 py-2 "
                        + (parent ? "mr-5 border-r-4 border-r-orange-200 " : "")
                        + (disabled ? "opacity-60" : "")
                      }
                      data-testid={parent ? `permission-child-${k}` : `permission-${k}`}
                    >
                      <span className="text-sm">{label}</span>
                      <input
                        type="checkbox"
                        checked={!!f.permissions[k]}
                        onChange={e => setPerm(k, e.target.checked)}
                        disabled={disabled}
                        className="h-5 w-5 accent-orange-600 disabled:cursor-not-allowed"
                        data-testid={`perm-${k}`}
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <button disabled={busy} onClick={save} className="mt-4 w-full pill brand-grad text-white font-bold py-3 tap-target disabled:opacity-60" data-testid="emp-save">
          {busy ? "جاري…" : "حفظ الصلاحيات"}
        </button>
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", tid, disabled }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input type={type} value={v} onChange={e => on(e.target.value)} disabled={disabled} data-testid={tid}
        className="mt-1 w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:opacity-70" />
    </label>
  );
}
